// Controlled-input handlers that format currency as the user types.

/** A field's formatted text, or `null` once the user clears it. */
export type CurrencyFieldState = Record<string, string | null>

/**
 * Called with the parsed number once the input is valid — `null` when the field
 * was cleared, which callers must treat as zero rather than pass on blindly.
 */
export type CurrencyInputCallback = (numericValue: number | null, fieldName: string) => void

export const handleSimpleCurrencyInput = (
  inputValue: string,
  setTextState: (value: string) => void,
  setNumericState: (value: number) => void
) => {
  const rawValue = inputValue.replaceAll(',', '')

  // Allow empty input
  if (rawValue === '') {
    setTextState('')
    setNumericState(0)
    return ''
  }

  // Validate input format
  if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
    // Handle decimal point cases
    if (rawValue.endsWith('.')) {
      setTextState(rawValue)
      return rawValue
    }

    // Format the value
    const parts = rawValue.split('.')
    const integerPart = parts[0] || '0'
    const decimalPart = parts[1] || ''
    const formattedInteger = new Intl.NumberFormat('en-US').format(Number(integerPart))
    const formattedValue =
      decimalPart.length > 0 ? `${formattedInteger}.${decimalPart}` : formattedInteger

    // Calculate numeric value
    const numericValue =
      decimalPart.length > 0
        ? Number.parseFloat(`${integerPart}.${decimalPart}`)
        : Number.parseFloat(integerPart)

    setTextState(formattedValue)
    setNumericState(numericValue)

    return formattedValue
  }

  // Return current value if input is invalid
  return inputValue
}

export const handleObjectCurrencyInput = <S extends CurrencyFieldState>(
  inputValue: string,
  fieldName: string,
  state: S,
  setState: (value: NoInfer<S>) => void,
  callback?: CurrencyInputCallback
) => {
  const rawValue = inputValue.replaceAll(',', '')

  // Allow empty input
  if (rawValue === '') {
    const newState = { ...state, [fieldName]: null } as S
    setState(newState)
    if (callback) callback(null, fieldName)
    return ''
  }

  // Validate input format
  if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
    // Handle decimal point cases
    if (rawValue.endsWith('.')) {
      const newState = { ...state, [fieldName]: rawValue } as S
      setState(newState)
      return rawValue
    }

    // Format the value
    const parts = rawValue.split('.')
    const integerPart = parts[0] || '0'
    const decimalPart = parts[1] || ''
    const formattedInteger = new Intl.NumberFormat('en-US').format(Number(integerPart))
    const formattedValue =
      decimalPart.length > 0 ? `${formattedInteger}.${decimalPart}` : formattedInteger

    // Calculate numeric value
    const numericValue =
      decimalPart.length > 0
        ? Number.parseFloat(`${integerPart}.${decimalPart}`)
        : Number.parseFloat(integerPart)

    const newState = { ...state, [fieldName]: formattedValue } as S
    setState(newState)

    // Execute callback if provided
    if (callback) callback(numericValue, fieldName)

    return formattedValue
  }

  // Return current value if input is invalid
  return state[fieldName] ?? ''
}
